import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Eye, Pencil, Copy, Trash2, ExternalLink, MoreVertical, Star } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import { StatusBadge } from "@/pages/admin/Dashboard";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUSES = [
  { value: "pubblicata", label: "Pubblicata" },
  { value: "bozza", label: "Bozza" },
  { value: "non_pubblicata", label: "Non pubblicata" },
  { value: "sospesa", label: "Sospesa" },
];

export default function Properties() {
  const [props, setProps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    api.get("/admin/properties").then((r) => setProps(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const changeStatus = async (id, status) => {
    await api.patch(`/admin/properties/${id}/status`, { status });
    toast.success("Stato aggiornato");
    load();
  };

  const duplicate = async (id) => {
    const { data } = await api.post(`/admin/properties/${id}/duplicate`);
    toast.success("Struttura duplicata");
    navigate(`/admin/properties/${data.id}/edit`);
  };

  const doDelete = async () => {
    await api.delete(`/admin/properties/${toDelete.id}`);
    toast.success("Struttura eliminata");
    setToDelete(null);
    load();
  };

  return (
    <AdminLayout title="Gestione strutture">
      <div className="flex justify-between items-center mb-6">
        <p className="text-brand-plum/60">{props.length} strutture nel network</p>
        <Link to="/admin/properties/new">
          <Button data-testid="properties-new-btn" className="rounded-full bg-brand-plum hover:bg-brand-violet text-white px-6">
            <Plus size={18} className="mr-1" /> Nuova casa vacanza
          </Button>
        </Link>
      </div>

      <div className="rounded-2xl bg-white border border-brand-plum/8 shadow-sm overflow-hidden" data-testid="admin-property-table-view">
        <table className="w-full">
          <thead className="bg-brand-warm/60 text-left text-xs uppercase tracking-wider text-brand-plum/50">
            <tr>
              <th className="p-4 font-medium">Struttura</th>
              <th className="p-4 font-medium hidden md:table-cell">Città</th>
              <th className="p-4 font-medium hidden lg:table-cell">Proprietario</th>
              <th className="p-4 font-medium">Stato</th>
              <th className="p-4 font-medium text-right">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-plum/8">
            {loading ? (
              <tr><td colSpan={5} className="p-10 text-center text-brand-plum/40">Caricamento...</td></tr>
            ) : props.map((p) => (
              <tr key={p.id} className="hover:bg-brand-warm/30" data-testid={`property-row-${p.slug}`}>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <img src={(p.photos?.find((x) => x.is_main) || p.photos?.[0])?.url} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-brand-plum truncate">{p.name}</p>
                      <p className="text-xs text-brand-plum/40 font-mono truncate">/{p.slug}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4 text-sm text-brand-plum/70 hidden md:table-cell">{p.city}</td>
                <td className="p-4 text-sm text-brand-plum/70 hidden lg:table-cell">{p.owner?.first_name} {p.owner?.last_name}</td>
                <td className="p-4">
                  <Select value={p.status} onValueChange={(v) => changeStatus(p.id, v)}>
                    <SelectTrigger data-testid={`property-status-toggle-${p.slug}`} className="w-[150px] h-9 border-brand-plum/15 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-1">
                    <Link to={`/proprieta/${p.slug}`} target="_blank" title="Anteprima">
                      <Button variant="ghost" size="icon" className="text-brand-plum/60 hover:text-brand-teal"><Eye size={17} /></Button>
                    </Link>
                    <Link to={`/admin/properties/${p.id}/edit`} title="Modifica" data-testid={`property-edit-${p.slug}`}>
                      <Button variant="ghost" size="icon" className="text-brand-plum/60 hover:text-brand-plum"><Pencil size={16} /></Button>
                    </Link>
                    <Button onClick={() => navigate(`/admin/properties/${p.id}/reviews`)} title="Recensioni" data-testid={`property-reviews-${p.slug}`} variant="outline" size="sm" className="border-brand-teal/40 text-brand-tealDark hover:bg-brand-tealLight gap-1.5 hidden sm:inline-flex">
                      <Star size={15} /> Recensioni
                    </Button>
                    <Button onClick={() => navigate(`/admin/properties/${p.id}/reviews`)} title="Recensioni" variant="ghost" size="icon" className="text-brand-tealDark sm:hidden"><Star size={16} /></Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-brand-plum/60"><MoreVertical size={17} /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => duplicate(p.id)} data-testid={`property-duplicate-${p.slug}`}><Copy size={15} className="mr-2" /> Duplica</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => window.open(`/proprieta/${p.slug}`, "_blank")}><ExternalLink size={15} className="mr-2" /> Apri mini-sito</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setToDelete(p)} className="text-red-500"><Trash2 size={15} className="mr-2" /> Elimina</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare "{toDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>Questa azione è irreversibile e rimuoverà anche il relativo mini-sito.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-property">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
